import {
  MAX_FILTER_DEPTH,
  MAX_GROUP_CHILDREN,
} from "@hightouch/lib/ad-studio/insights-malloy/constants";
import { err, isErr, isOk, ok } from "@hightouch/lib/util/result";
import type { KeyboardEvent } from "react";
import { z } from "zod/v4";
import type {
  Advanced,
  AppliedGroup,
  Applies,
  CheckedAdvanced,
  CheckGroup,
  DraftCondition,
  DraftGroup,
  DraftLeaf,
  DraftNode,
  DraftRoot,
  FieldOperand,
  LeafView,
  Logic,
  NumberFormat,
  OperandSpec,
  OperatorChoice,
  OperatorChoiceOption,
  OperatorVocabulary,
  PillSummary,
  ValueOption,
  FieldRole,
  FilterBarDraft,
  FilterField,
  FilterFolders,
  CompiledGroup,
} from "src/pages/ad-studio/filters/types";

/** Local-only id for React keys and addressing edits; never serialized. */
const nextId = () => crypto.randomUUID();

export const toDraftLeaf = (condition: DraftCondition): DraftLeaf => ({
  ...condition,
  id: nextId(),
});

const RELATIVE_DAYS = /^last (.*) days$/;
const relativeDays = (days: string) => `last ${days} days`;

export const lastDaysCondition = (member: string): DraftCondition => ({
  member,
  operator: "inDateRange",
  values: [relativeDays("")],
});

export const relativeDaysOf = (
  leaf: DraftCondition,
): { kind: "relative"; days: string } | { kind: "not-relative" } => {
  if (leaf.operator !== "inDateRange") {
    return { kind: "not-relative" };
  }
  const days = RELATIVE_DAYS.exec(leaf.values[0] ?? "")?.[1];
  return days === undefined
    ? { kind: "not-relative" }
    : { kind: "relative", days };
};

export const valuesOf = (leaf: DraftCondition): string[] => {
  switch (leaf.operator) {
    case "set":
    case "notSet": {
      return [];
    }
    default: {
      return leaf.values;
    }
  }
};

export const operatorChoiceOf = (leaf: DraftLeaf): OperatorChoice =>
  relativeDaysOf(leaf).kind === "relative" ? "last-n-days" : leaf.operator;

const withDays = ({ leaf, days }: { leaf: DraftLeaf; days: string }) => ({
  id: leaf.id,
  member: leaf.member,
  operator: "inDateRange" as const,
  values: [relativeDays(days)],
});

const EMPTY_VALUES: Record<OperandSpec["kind"], readonly string[]> = {
  "no-operand": [],
  days: [""],
  number: [""],
  choices: [],
  creatable: [],
  boolean: ["true"],
  text: [""],
  date: [""],
  "date-range": ["", ""],
};

/** A value the new operator still takes is kept. */
export function withOperator({
  leaf,
  choice,
  operandKindOf,
}: {
  leaf: DraftLeaf;
  choice: OperatorChoice;
  operandKindOf: (leaf: DraftLeaf) => OperandSpec["kind"];
}): DraftLeaf {
  const { id, member } = leaf;
  const days = relativeDaysOf(leaf);
  if (choice === "last-n-days") {
    return withDays({ leaf, days: days.kind === "relative" ? days.days : "" });
  }
  if (choice === "set" || choice === "notSet") {
    return { id, member, operator: choice };
  }
  const candidate = {
    id,
    member,
    operator: choice,
    values: days.kind === "relative" ? [] : valuesOf(leaf),
  };
  const kind = operandKindOf(candidate);
  return days.kind === "not-relative" && kind === operandKindOf(leaf)
    ? candidate
    : { ...candidate, values: [...EMPTY_VALUES[kind]] };
}

export function withValues({
  leaf,
  values,
}: {
  leaf: DraftLeaf;
  values: readonly string[];
}): DraftLeaf {
  if (relativeDaysOf(leaf).kind === "relative") {
    return withDays({ leaf, days: values[0] ?? "" });
  }
  switch (leaf.operator) {
    case "set":
    case "notSet": {
      return leaf;
    }
    default: {
      return { ...leaf, values: [...values] };
    }
  }
}

export const isDraftGroup = (node: DraftNode): node is DraftGroup =>
  "and" in node || "or" in node;

export const logicOf = (group: DraftGroup): Logic =>
  "and" in group ? "and" : "or";

export const childrenOf = (group: DraftGroup): readonly DraftNode[] =>
  "and" in group ? group.and : group.or;

const toDraftGroup = ({
  id,
  logic,
  children,
}: {
  id: string;
  logic: Logic;
  children: readonly DraftNode[];
}): DraftGroup =>
  logic === "and" ? { id, and: children } : { id, or: children };

const withChildren = (
  group: DraftGroup,
  children: readonly DraftNode[],
): DraftGroup =>
  toDraftGroup({ id: group.id, logic: logicOf(group), children });

export const emptyGroup = ({
  logic,
  children,
}: {
  logic: Logic;
  children: readonly DraftNode[];
}): DraftGroup => toDraftGroup({ id: nextId(), logic, children });

export const leavesOf = (node: DraftNode): readonly DraftLeaf[] =>
  isDraftGroup(node) ? childrenOf(node).flatMap(leavesOf) : [node];

const updateGroup = ({
  group,
  id,
  update,
}: {
  group: DraftGroup;
  id: string;
  update: (group: DraftGroup) => DraftGroup;
}): DraftGroup =>
  group.id === id
    ? update(group)
    : withChildren(
        group,
        childrenOf(group).map((child) =>
          isDraftGroup(child)
            ? updateGroup({ group: child, id, update })
            : child,
        ),
      );

const depthOf = (group: DraftGroup): number =>
  1 +
  Math.max(
    0,
    ...childrenOf(group).map((child) =>
      isDraftGroup(child) ? depthOf(child) : 0,
    ),
  );

export const appendChild = ({
  group,
  parentId,
  child,
}: {
  group: DraftGroup;
  parentId: string;
  child: DraftNode;
}): DraftGroup => {
  const next = updateGroup({
    group,
    id: parentId,
    update: (parent) =>
      childrenOf(parent).length < MAX_GROUP_CHILDREN
        ? withChildren(parent, [...childrenOf(parent), child])
        : parent,
  });
  return depthOf(next) > MAX_FILTER_DEPTH ? group : next;
};

export const setLogic = ({
  group,
  groupId,
  logic,
}: {
  group: DraftGroup;
  groupId: string;
  logic: Logic;
}): DraftGroup =>
  updateGroup({
    group,
    id: groupId,
    update: (target) =>
      toDraftGroup({ id: target.id, logic, children: childrenOf(target) }),
  });

export const replaceLeaf = (group: DraftGroup, leaf: DraftLeaf): DraftGroup =>
  withChildren(
    group,
    childrenOf(group).map((child) =>
      child.id === leaf.id
        ? leaf
        : isDraftGroup(child)
          ? replaceLeaf(child, leaf)
          : child,
    ),
  );

export const removeNode = (group: DraftGroup, id: string): DraftGroup =>
  withChildren(
    group,
    childrenOf(group).flatMap((child) =>
      child.id === id
        ? []
        : [isDraftGroup(child) ? removeNode(child, id) : child],
    ),
  );

const retargetedLeaf = ({
  leaf,
  members,
}: {
  leaf: DraftLeaf;
  members: ReadonlyMap<string, string>;
}): readonly DraftLeaf[] => {
  const member = members.get(leaf.member);
  return member === undefined ? [] : [{ ...leaf, member }];
};

// Dropping a disjunct would narrow the filter, so an or that loses one is dropped whole.
function retargetGroup({
  group,
  members,
}: {
  group: DraftGroup;
  members: ReadonlyMap<string, string>;
}): readonly DraftGroup[] {
  const children = childrenOf(group);
  const kept = children.flatMap((child): readonly DraftNode[] =>
    isDraftGroup(child)
      ? retargetGroup({ group: child, members })
      : retargetedLeaf({ leaf: child, members }),
  );
  return kept.length === 0 ||
    (logicOf(group) === "or" && kept.length < children.length)
    ? []
    : [withChildren(group, kept)];
}

export const appliesOf = ({
  leaf,
  leafView,
}: {
  leaf: DraftLeaf;
  leafView: (leaf: DraftLeaf) => LeafView | "unknown-member";
}): Applies => {
  const view = leafView(leaf);
  return view === "unknown-member" ? "does-not-apply" : view.applies;
};

// htui's Select and MultiSelect let Escape bubble from an open menu, and Chakra's popover skips closing on a prevented Escape.
export const keepEscapeInOpenSelect = (
  event: Pick<KeyboardEvent, "key" | "target" | "preventDefault">,
): void => {
  if (
    event.key === "Escape" &&
    event.target instanceof Element &&
    event.target.getAttribute("aria-expanded") === "true"
  ) {
    event.preventDefault();
  }
};

export const EMPTY_DRAFT: FilterBarDraft = {
  conditions: [],
  advanced: "no-advanced-group",
  menu: { popover: { kind: "closed" } },
  pillEdit: "no-pill-edit",
  notices: [],
};

export const fieldsOf = (folders: FilterFolders): readonly FilterField[] => {
  switch (folders.status) {
    case "loading":
    case "failed": {
      return [];
    }
    case "loaded": {
      return folders.folders.flatMap((folder) => folder.fields);
    }
    default: {
      return folders satisfies never;
    }
  }
};

const NUMBER_AFFIXES: Record<NumberFormat, { prefix: string; suffix: string }> =
  {
    "currency-compact": { prefix: "$", suffix: "" },
    "currency-precise": { prefix: "$", suffix: "" },
    percent: { prefix: "", suffix: "%" },
    "number-compact": { prefix: "", suffix: "" },
    frequency: { prefix: "", suffix: "x" },
    seconds: { prefix: "", suffix: " seconds" },
  };

export const numberAffixes = (
  format: NumberFormat,
): { prefix: string; suffix: string } => NUMBER_AFFIXES[format];

export const numberText = ({
  value,
  format,
}: {
  value: string;
  format: NumberFormat;
}): string => {
  const { prefix, suffix } = NUMBER_AFFIXES[format];
  return value === "" ? "" : `${prefix}${value}${suffix}`;
};

type Operator = Exclude<OperatorChoice, "last-n-days">;
type OperatorText = { label: string; summary: string };

const OPERATOR_TEXT: Record<Operator, OperatorText> = {
  equals: { label: "Is any of", summary: "is any of" },
  notEquals: { label: "Is not any of", summary: "is not any of" },
  contains: { label: "Contains", summary: "contains" },
  notContains: { label: "Does not contain", summary: "does not contain" },
  startsWith: { label: "Starts with", summary: "starts with" },
  notStartsWith: {
    label: "Does not start with",
    summary: "does not start with",
  },
  endsWith: { label: "Ends with", summary: "ends with" },
  notEndsWith: { label: "Does not end with", summary: "does not end with" },
  gt: { label: "Is more than", summary: ">" },
  gte: { label: "Is at least", summary: "≥" },
  lt: { label: "Is less than", summary: "<" },
  lte: { label: "Is at most", summary: "≤" },
  inDateRange: { label: "Is between", summary: "between" },
  notInDateRange: { label: "Is not between", summary: "not between" },
  beforeDate: { label: "Is before", summary: "before" },
  beforeOrOnDate: { label: "Is on or before", summary: "on or before" },
  afterDate: { label: "Is after", summary: "after" },
  afterOrOnDate: { label: "Is on or after", summary: "on or after" },
  set: { label: "Is set", summary: "is set" },
  notSet: { label: "Is not set", summary: "is not set" },
};

const VOCABULARIES: Record<
  OperatorVocabulary,
  Record<Operator, OperatorText>
> = {
  plain: OPERATOR_TEXT,
  number: {
    ...OPERATOR_TEXT,
    equals: { label: "Is equal to", summary: "=" },
    notEquals: { label: "Is not equal to", summary: "≠" },
  },
  time: {
    ...OPERATOR_TEXT,
    equals: { label: "Is on", summary: "on" },
    notEquals: { label: "Is not on", summary: "not on" },
  },
  tag: {
    ...OPERATOR_TEXT,
    equals: { label: "Includes", summary: "includes" },
    notEquals: { label: "Does not include", summary: "does not include" },
    set: { label: "Has any value", summary: "has any value" },
    notSet: { label: "Has no value", summary: "has no value" },
  },
  boolean: { ...OPERATOR_TEXT, equals: { label: "Is", summary: "" } },
};

const LAST_N_DAYS_TEXT: OperatorText = {
  label: "In the last",
  summary: "in the last",
};

export const operatorChoiceOptions = ({
  vocabulary,
  operators,
}: {
  vocabulary: OperatorVocabulary;
  operators: readonly Operator[];
}): readonly OperatorChoiceOption[] => [
  ...(vocabulary === "time"
    ? [{ choice: "last-n-days" as const, label: LAST_N_DAYS_TEXT.label }]
    : []),
  ...operators.map((operator) => ({
    choice: operator,
    label: VOCABULARIES[vocabulary][operator].label,
  })),
];

export const emptyConditionOf = ({
  member,
  operand,
}: {
  member: string;
  operand: FieldOperand;
}): DraftCondition => {
  switch (operand.kind) {
    case "choices":
    case "creatable": {
      return { member, operator: "equals", values: [] };
    }
    case "date": {
      return lastDaysCondition(member);
    }
    case "number": {
      return { member, operator: "gte", values: [""] };
    }
    case "free-text": {
      return { member, operator: "contains", values: [""] };
    }
    case "boolean": {
      return { member, operator: "equals", values: ["true"] };
    }
    default: {
      return operand satisfies never;
    }
  }
};

const withPickedOptions = ({
  options,
  values,
}: {
  options: readonly ValueOption[];
  values: readonly string[];
}): readonly ValueOption[] => {
  const known = new Set(options.map((option) => option.value));
  return [
    ...options,
    ...values
      .filter((value) => !known.has(value))
      .map((value) => ({ value, label: value })),
  ];
};

const BOOLEAN_OPTIONS: readonly ValueOption[] = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const binaryOperandSpec = ({
  leaf,
  operand,
}: {
  leaf: DraftLeaf & { values: string[] };
  operand: FieldOperand;
}): OperandSpec => {
  const { operator, values } = leaf;
  const [first = "", second = ""] = values;
  switch (operand.kind) {
    case "choices": {
      return {
        kind: "choices",
        values,
        options: withPickedOptions({ options: operand.options, values }),
      };
    }
    case "creatable": {
      return { kind: "creatable", values };
    }
    case "free-text": {
      return operator === "equals" || operator === "notEquals"
        ? { kind: "creatable", values }
        : { kind: "text", value: first };
    }
    case "number": {
      return { kind: "number", value: first, format: operand.format };
    }
    case "date": {
      return operator === "inDateRange" || operator === "notInDateRange"
        ? { kind: "date-range", from: first, to: second }
        : { kind: "date", value: first };
    }
    case "boolean": {
      return { kind: "boolean", value: first === "false" ? "false" : "true" };
    }
    default: {
      return operand satisfies never;
    }
  }
};

export const operandSpecOf = ({
  leaf,
  operand,
}: {
  leaf: DraftLeaf;
  operand: FieldOperand;
}): OperandSpec => {
  switch (leaf.operator) {
    case "set":
    case "notSet": {
      return { kind: "no-operand" };
    }
    default: {
      const days = relativeDaysOf(leaf);
      return days.kind === "relative"
        ? { kind: "days", days: days.days }
        : binaryOperandSpec({ leaf, operand });
    }
  }
};

const toPillText = (summary: string, value: string): PillSummary => ({
  kind: "text",
  text: `${summary} ${value}`.trim(),
  labelSuffix: "",
});

const listText = ({
  summary,
  texts,
}: {
  summary: string;
  texts: readonly string[];
}): PillSummary =>
  texts.length > 1
    ? { kind: "count", count: texts.length }
    : toPillText(summary, texts.join(", "));

const choiceLabel = (options: readonly ValueOption[], value: string): string =>
  options.find((option) => option.value === value)?.label ?? value;

export const leafSummary = ({
  leaf,
  operand,
  vocabulary,
  daysText,
}: {
  leaf: DraftLeaf;
  operand: FieldOperand;
  vocabulary: OperatorVocabulary;
  daysText: (days: string) => string;
}): PillSummary => {
  const spec = operandSpecOf({ leaf, operand });
  const { summary } = VOCABULARIES[vocabulary][leaf.operator];
  switch (spec.kind) {
    case "no-operand": {
      return toPillText(summary, "");
    }
    case "days": {
      return toPillText(LAST_N_DAYS_TEXT.summary, daysText(spec.days));
    }
    case "number": {
      return toPillText(summary, numberText(spec));
    }
    case "choices": {
      return listText({
        summary,
        texts: spec.values.map((value) => choiceLabel(spec.options, value)),
      });
    }
    case "creatable": {
      return listText({ summary, texts: spec.values });
    }
    case "boolean": {
      return {
        kind: "text",
        text: choiceLabel(BOOLEAN_OPTIONS, spec.value),
        labelSuffix: ":",
      };
    }
    case "text":
    case "date": {
      return toPillText(summary, spec.value);
    }
    case "date-range": {
      return toPillText(summary, `${spec.from} - ${spec.to}`);
    }
    default: {
      return spec satisfies never;
    }
  }
};

const closedOn = (applied: AppliedGroup | "no-advanced-group"): Advanced =>
  applied === "no-advanced-group" ? applied : { applied, popover: "closed" };

const retargetedAdvanced = ({
  advanced,
  members,
  checkGroup,
}: {
  advanced: Advanced;
  members: ReadonlyMap<string, string>;
  checkGroup: CheckGroup;
}): Advanced => {
  if (advanced === "no-advanced-group") {
    return advanced;
  }
  const [running = "no-advanced-group"] = (
    advanced.applied === "no-advanced-group"
      ? []
      : retargetGroup({ group: advanced.applied.group, members })
  ).flatMap((group) => {
    const checked = checkGroup(group);
    return isOk(checked) ? [checked.value] : [];
  });
  const [draft] =
    advanced.popover === "closed"
      ? []
      : retargetGroup({ group: advanced.popover.draft, members });
  return draft === undefined
    ? closedOn(running)
    : { applied: running, popover: { draft } };
};

export const toRetargetedDraft = ({
  draft,
  members,
  checkGroup,
}: {
  draft: FilterBarDraft;
  members: ReadonlyMap<string, string>;
  checkGroup: CheckGroup;
}): FilterBarDraft => ({
  ...draft,
  conditions: draft.conditions.flatMap((leaf) =>
    retargetedLeaf({ leaf, members }),
  ),
  advanced: retargetedAdvanced({
    advanced: draft.advanced,
    members,
    checkGroup,
  }),
  pillEdit: "no-pill-edit",
});

const prunedGroup = ({
  group,
  applies,
}: {
  group: DraftGroup;
  applies: (leaf: DraftLeaf) => boolean;
}): readonly DraftGroup[] => {
  const kept = childrenOf(group).flatMap((child): readonly DraftNode[] => {
    if (!isDraftGroup(child)) {
      return applies(child) ? [trimmedLeaf(child)] : [];
    }
    return prunedGroup({ group: child, applies });
  });
  return kept.length === 0 ? [] : [withChildren(group, kept)];
};

const draftGroupShape = z.custom<DraftGroup>(
  (value) =>
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    ("and" in value || "or" in value),
);

export const toCheckGroup = ({
  applies,
  compile,
}: {
  applies: (leaf: DraftLeaf) => boolean;
  compile: (group: DraftGroup) => CompiledGroup;
}): CheckGroup => {
  const schema = draftGroupShape
    .transform((group, context) => {
      const compiled = compile(group);
      if (isErr(compiled)) {
        context.addIssue({ code: "custom", message: compiled.error.message });
        return z.NEVER;
      }
      return { group, filter: compiled.value };
    })
    .brand<"AppliedGroup">();
  return (group) => {
    const [pruned] = prunedGroup({ group, applies });
    if (pruned === undefined) {
      return err({ failure: "no-complete-rules" });
    }
    const parsed = schema.safeParse(pruned);
    return parsed.success
      ? ok(parsed.data)
      : err({
          failure: "cannot-run",
          message: parsed.error.issues.map(({ message }) => message).join(", "),
        });
  };
};

export const checkedAdvancedOf = ({
  advanced,
  checkGroup,
}: {
  advanced: Advanced;
  checkGroup: CheckGroup;
}): CheckedAdvanced =>
  advanced === "no-advanced-group" || advanced.popover === "closed"
    ? advanced
    : {
        applied: advanced.applied,
        popover: {
          draft: advanced.popover.draft,
          checked: checkGroup(advanced.popover.draft),
        },
      };

export const runningGroupOf = (
  checked: CheckedAdvanced,
): AppliedGroup | "no-advanced-group" => {
  if (checked === "no-advanced-group") {
    return checked;
  }
  if (checked.popover === "closed") {
    return checked.applied;
  }
  return isOk(checked.popover.checked)
    ? checked.popover.checked.value
    : checked.applied;
};

export const rootOf = ({
  draft,
  checked,
}: {
  draft: Pick<FilterBarDraft, "conditions">;
  checked: CheckedAdvanced;
}): DraftRoot => ({
  conditions: draft.conditions,
  advanced: runningGroupOf(checked),
});

export const trimmedLeaf = (leaf: DraftLeaf): DraftLeaf => {
  switch (leaf.operator) {
    case "set":
    case "notSet": {
      return leaf;
    }
    default: {
      return {
        ...leaf,
        values: leaf.values.flatMap((value) =>
          value.trim() === "" ? [] : [value.trim()],
        ),
      };
    }
  }
};

export const roleOfField = ({
  folders,
  member,
}: {
  folders: FilterFolders;
  member: string;
}): readonly FieldRole[] =>
  fieldsOf(folders).flatMap((field) =>
    field.initial.member === member ? [field.role] : [],
  );
